import { useEffect, useRef, useState } from 'react';
import {
  TextureLoader,
  BackSide,
  RepeatWrapping,
  Texture,
  ShaderMaterial,
} from 'three';
import useEquirectangularStore, { isSamePosition } from '@/store/viewer';
import { Panorama } from '@/modules/defines/interface';
import usePanoramaStore from '@/store/panorama';
import { normalizeAngle } from '@/utils/function/util';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

interface EquirectangularViewerProps {
  imageUrl: string;
  imageThumbnail: string;
  position: [number, number, number];
  panorama: Panorama;
  viewerKey: string;
  onLoad?: () => void;
  initialAngle: number;
  isMyRoom?: boolean;
  coordToLook?: number[];
}

const EquirectangularViewer: React.FC<EquirectangularViewerProps> = ({
  imageUrl,
  imageThumbnail,
  position,
  panorama,
  viewerKey,
  onLoad,
  initialAngle = 0,
  isMyRoom,
  coordToLook,
}) => {
  const [currentTexture, setCurrentTexture] = useState<Texture | null>(null);
  const [thumbnailTexture, setThumbnailTexture] = useState<Texture | null>(
    null
  );
  const [originalTexture, setOriginalTexture] = useState<Texture | null>(null);

  const hasLoaded = useRef(false);
  const currentPosition = usePanoramaStore((state) => state.currentPosition);

  const initializeViewer = useEquirectangularStore(
    (state) => state.initializeViewer
  );

  const updateViewer = useEquirectangularStore((state) => state.updateViewer);

  const viewer = useEquirectangularStore((state) => state.viewers[viewerKey]);
  const isDragging = useEquirectangularStore((state) => state.isDragging);

  const { camera, gl } = useThree();

  const targetQuaternionRef = useRef<THREE.Quaternion | null>(null);
  const rotatingRef = useRef(false);
  const startRotationRef = useRef(false);

  useEffect(() => {
    if (isMyRoom) {
      initializeViewer(viewerKey, position, panorama, [0, 0, 0]);
    } else {
      initializeViewer(viewerKey, position, panorama, currentPosition);
      if (isSamePosition(position, currentPosition)) {
        updateViewer(viewerKey, {
          visible: true,
          radius: 1,
          opacity: 1,
        });
      }
    }
  }, [viewerKey, initializeViewer]);

  useEffect(() => {
    const loader = new TextureLoader();
    loader.load(
      imageThumbnail,
      (texture) => {
        if (!hasLoaded.current && onLoad) {
          texture.wrapS = RepeatWrapping;
          texture.repeat.x = -1;

          const offsetX = normalizeAngle(initialAngle) / 360;
          texture.offset.x = offsetX;

          setCurrentTexture(texture);
          setThumbnailTexture(texture);
          hasLoaded.current = true;
          if (onLoad) onLoad();
        }
      },
      undefined,
      (error) => {
        console.error('Error loading texture:', error);
      }
    );
  }, [imageThumbnail, initialAngle, onLoad]);

  useEffect(() => {
    if (viewer?.visible && thumbnailTexture) {
      if (!originalTexture) {
        const loader = new TextureLoader();
        loader.load(imageUrl, (texture) => {
          texture.wrapS = RepeatWrapping;
          texture.repeat.x = -1;

          const offsetX = normalizeAngle(initialAngle) / 360;
          texture.offset.x = offsetX;
          setOriginalTexture(texture);
          setCurrentTexture(texture);

          if (camera && coordToLook) {
            if (
              coordToLook[0] !== 0 ||
              coordToLook[1] !== 0 ||
              coordToLook[2] !== 0
            ) {
              const targetPosition = new THREE.Vector3(
                coordToLook[0],
                position[1],
                coordToLook[2]
              );
              const viewerPosition = new THREE.Vector3(
                position[0],
                position[1],
                position[2]
              );

              // 1️⃣ 현재 카메라의 forward 방향 벡터
              const cameraDirection = new THREE.Vector3(
                0,
                0,
                -1
              ).applyQuaternion(camera.quaternion);

              // 2️⃣ 타겟 방향 벡터 (타겟 - 현재 카메라 위치)
              const targetDirection = new THREE.Vector3()
                .subVectors(targetPosition, viewerPosition)
                .normalize();

              // 3️⃣ 두 벡터 사이의 회전 각도 계산
              const angle = cameraDirection.angleTo(targetDirection);

              // 4️⃣ 회전 축 계산 (외적 이용)
              const rotationAxis = new THREE.Vector3()
                .crossVectors(cameraDirection, targetDirection)
                .setX(0)
                .setZ(0)
                .normalize();

              const quaternion = new THREE.Quaternion();
              quaternion.setFromAxisAngle(rotationAxis, angle);

              const targetQuat = camera.quaternion
                .clone()
                .premultiply(quaternion);
              targetQuaternionRef.current = targetQuat;
              rotatingRef.current = true;
              startRotationRef.current = false;
            }
          }
        });
      } else {
        setCurrentTexture(originalTexture);
      }
    }
  }, [viewer, imageUrl, thumbnailTexture, initialAngle, originalTexture]);

  useFrame(() => {
    if (!camera || !coordToLook) return;

    if (!startRotationRef.current) {
      const distance = camera.position.distanceTo(
        new THREE.Vector3(...position)
      );
      if (distance < 0.1) {
        startRotationRef.current = true;
      } else {
        return; // 아직 위치 이동 안 끝남
      }
    }

    if (!rotatingRef.current || !targetQuaternionRef.current) return;

    camera.quaternion.slerp(targetQuaternionRef.current, 0.05);

    if (
      camera.quaternion.angleTo(targetQuaternionRef.current) < 0.01 ||
      isDragging
    ) {
      camera.quaternion.copy(targetQuaternionRef.current);
      rotatingRef.current = false;
      targetQuaternionRef.current = null;
      camera.updateProjectionMatrix();
    }
  });

  if (!viewer || !currentTexture) return null;

  const offsetX = normalizeAngle(initialAngle) / 360; // 회전 값 계산
  const material = new ShaderMaterial({
    uniforms: {
      tex: { value: currentTexture },
      opacity: { value: viewer.opacity },
      blurStrength: { value: 1.0 }, // 블러 강도 (조절 가능)
      offsetX: { value: offsetX }, // 회전 값 추가
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D tex;
      uniform float opacity;
      uniform float blurStrength;
      uniform float offsetX;  // 회전 값
      varying vec2 vUv;

      void main() {
        vec2 uv = vUv;
        uv.x = 1.0 - uv.x;  // 반전 처리
        uv.x = uv.x + offsetX;  // 회전 값을 0~1 범위로 클리핑
        
        vec4 color = texture2D(tex, uv);
        
        // 하단 블러 처리
        float blurFactor = smoothstep(0.4, 0.0, vUv.y) * blurStrength;
        vec4 blurColor = vec4(0.7, 0.7, 0.7, 1.0); // 흐릿한 색상
        vec4 finalColor = mix(color, blurColor, blurFactor);

        finalColor.a *= opacity;  // 투명도 적용
        if (finalColor.a < 0.01) discard;  // 투명 픽셀 제거
        gl_FragColor = finalColor;
      }
    `,
    transparent: true,
    side: BackSide,
    depthTest: false,
    depthWrite: false,
  });

  return (
    <mesh
      position={position}
      name="EquirectangularViewer"
      visible={viewer.visible}
    >
      <sphereGeometry args={[viewer.radius, 60, 40]} />
      <primitive object={material} attach="material" />
    </mesh>
  );
};

export default EquirectangularViewer;
